// components/profile/utils/validation.js

// Email validation regex
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// URL validation regex
const URL_REGEX = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

// Required fields configuration
export const REQUIRED_FIELDS = {
  // Basic Info
  companyName: 'Company name is required',
  location: 'Location is required',
  email: 'Email address is required',
  introParagraph: 'Introduction is required',
  startingPrice: 'Starting price is required',
  minimumTimeframe: 'Timeframe is required',
  
  // About You
  headerImage: 'Header image is required',
  firstName: 'First name is required',
  surname: 'Surname is required',
  profilePhoto: 'Profile photo is required',
  brandIcon: 'Brand icon is required',
  title: 'Professional title is required',
  bio: 'Biography is required',
  
  // Portfolio (tags are required)
  tags: 'Please select at least one business tag'
};

// Field-specific validation rules (format validation only - always applied)
const FIELD_VALIDATORS = {
  email: (value) => {
    if (!value) return null; // Skip if empty (required check handles this)
    if (!EMAIL_REGEX.test(value)) {
      return 'Please enter a valid email address';
    }
    return null;
  },

  website: (value) => {
    if (!value) return null;
    if (!URL_REGEX.test(value.trim())) {
      return 'Please enter a valid website URL';
    }
    return null;
  },

  instagram: (value) => {
    if (!value) return null;
    if (!URL_REGEX.test(value.trim())) {
      return 'Please enter a valid Instagram URL';
    }
    return null;
  },
  
  startingPrice: (value) => {
    if (!value) return null; // Skip if empty
    const price = parseFloat(value);
    if (isNaN(price) || price < 0) {
      return 'Price must be a positive number';
    }
    if (price > 1000000) {
      return 'Price seems too high. Please check';
    }
    return null;
  },
  
  minimumTimeframe: (value) => {
    if (!value) return null; // Skip if empty
    const weeks = parseInt(value);
    if (isNaN(weeks) || weeks < 1) {
      return 'Timeframe must be at least 1 week';
    }
    if (weeks > 52) {
      return 'Timeframe cannot exceed 52 weeks';
    }
    return null;
  },
  
  introParagraph: (value, _formData, _isActive = true) => {
    if (!value) return null;
    if (value.length < 50) {
      return 'Introduction should be at least 50 characters';
    }
    if (value.length > 500) {
      return 'Introduction cannot exceed 500 characters';
    }
    return null;
  },
  
  bio: (value, _formData, _isActive = true) => {
    if (!value) return null;
    if (value.length < 100) {
      return 'Biography should be at least 100 characters';
    }
    if (value.length > 1000) {
      return 'Biography cannot exceed 1000 characters';
    }
    return null;
  },
  
  videoUrl: (value) => {
    if (!value) return null; // Optional field
    if (!URL_REGEX.test(value)) {
      return 'Please enter a valid URL';
    }
    const supportedPlatforms = ['youtube.com', 'youtu.be', 'vimeo.com'];
    if (!supportedPlatforms.some(platform => value.includes(platform))) {
      return 'Please use a YouTube or Vimeo link';
    }
    return null;
  },
  
  // REMOVED: tags validator - it's a required field check, not a format validator
  // The required field logic handles tags validation conditionally based on isActive
  
  galleryPhotos: (value) => {
    if (!value) return null;
    if (Array.isArray(value) && value.length > 12) {
      return 'Gallery can have maximum 12 photos';
    }
    return null;
  },
  
  firstName: (value) => {
    if (!value) return null;
    if (value.length > 50) {
      return 'First name cannot exceed 50 characters';
    }
    return null;
  },
  
  surname: (value) => {
    if (!value) return null;
    if (value.length > 50) {
      return 'Surname cannot exceed 50 characters';
    }
    return null;
  },
  
  companyName: (value) => {
    if (!value) return null;
    if (value.length > 100) {
      return 'Company name cannot exceed 100 characters';
    }
    return null;
  },
  
  location: (value) => {
    if (!value) return null;
    if (value.length > 100) {
      return 'Location cannot exceed 100 characters';
    }
    return null;
  },
  
  title: (value) => {
    if (!value) return null;
    if (value.length > 100) {
      return 'Title cannot exceed 100 characters';
    }
    return null;
  }
};

// NEW: Validate a single field with conditional required check
export const validateField = (fieldName, value, formData = {}, isActive = true) => {
  // Check if field is required AND jeweller is active
  if (isActive && REQUIRED_FIELDS[fieldName]) {
    const isTagsObjectEmpty = fieldName === 'tags' && (!value || !Object.values(value).some(v => v === true));
    if (isTagsObjectEmpty || !value || (Array.isArray(value) && value.length === 0) || value === '') {
      return REQUIRED_FIELDS[fieldName];
    }
  }
  
  // Always apply field-specific validators (format checks)
  if (FIELD_VALIDATORS[fieldName]) {
    const error = FIELD_VALIDATORS[fieldName](value, formData, isActive);
    if (error) return error;
  }
  
  return null;
};

// NEW: Validate all fields with conditional required check
export const validateAllFields = (formData, isActive = true) => {
  const errors = {};
  
  // Validate all fields that have data
  Object.keys(formData).forEach(fieldName => {
    const error = validateField(fieldName, formData[fieldName], formData, isActive);
    if (error) {
      errors[fieldName] = error;
    }
  });
  
  // Only check for missing required fields if jeweller is active
  if (isActive) {
    Object.keys(REQUIRED_FIELDS).forEach(fieldName => {
      if (!errors[fieldName]) {
        const error = validateField(fieldName, formData[fieldName], formData, isActive);
        if (error) {
          errors[fieldName] = error;
        }
      }
    });
  }
  
  return errors;
};

// --- UPDATED AND SIMPLIFIED FUNCTION ---
// Calculate profile completion percentage based ONLY on required fields.
export const calculateProfileProgress = (formData) => {
  if (!formData) return 0;
  
  const requiredFields = Object.keys(REQUIRED_FIELDS);
  const totalRequired = requiredFields.length;

  if (totalRequired === 0) {
    return 100; // If no fields are required, profile is 100% complete.
  }

  // Count how many of the required fields are filled.
  const filledCount = requiredFields.filter(field => {
    const value = formData[field];
    
    // Special check for the 'tags' object
    if (field === 'tags') {
      return value && typeof value === 'object' && Object.values(value).some(v => v === true);
    }
    
    // General checks for other field types
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim() !== '';
    
    // Check for any other truthy value (like a number or a non-empty object)
    return !!value;
  }).length;
  
  // Return the simple percentage.
  return Math.round((filledCount / totalRequired) * 100);
};

// NEW: Get section-specific validation errors with conditional required check
export const getSectionErrors = (section, formData, isActive = true) => {
  const sectionFields = {
    basic: ['companyName', 'location', 'email', 'introParagraph', 'startingPrice', 'minimumTimeframe'],
    about: ['headerImage', 'firstName', 'surname', 'profilePhoto', 'brandIcon', 'title', 'bio'],
    portfolio: ['tags']
  };
  
  const fields = sectionFields[section] || [];
  const errors = {};
  
  fields.forEach(field => {
    const error = validateField(field, formData[field], formData, isActive);
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};

// NEW: Check if a section is complete with conditional required check
export const isSectionComplete = (section, formData, isActive = true) => {
  const errors = getSectionErrors(section, formData, isActive);
  return Object.keys(errors).length === 0;
};

// NEW: Get missing required fields (only relevant when active)
export const getMissingRequiredFields = (formData, isActive = true) => {
  if (!isActive) {
    return []; // No required fields when not active
  }
  
  const missing = [];
  
  Object.keys(REQUIRED_FIELDS).forEach(field => {
    const value = formData[field];
    let isMissing = false;
    
    if (field === 'tags') {
      isMissing = !value || !Object.values(value).some(v => v === true);
    } else {
      isMissing = !value || 
                  (Array.isArray(value) && value.length === 0) || 
                  (typeof value === 'string' && value.trim() === '') ||
                  (typeof value === 'number' && value === 0);
    }
    
    if (isMissing) {
      missing.push({
        field,
        label: REQUIRED_FIELDS[field].replace(' is required', '')
      });
    }
  });
  
  return missing;
};

// Sanitize form data before submission
export const sanitizeFormData = (formData) => {
  const sanitized = {};
  
  Object.keys(formData).forEach(key => {
    let value = formData[key];
    
    if (typeof value === 'string') {
      value = value.trim();
    }
    
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    
    if (value !== null && value !== undefined && value !== '') {
      sanitized[key] = value;
    }
  });
  
  return sanitized;
};
